/**
 * Version skew: this window's code against a backend that has since changed
 * (ADR 0103).
 *
 * # The signal
 *
 * Every backend response carries `X-App-Version` (the short git SHA of the
 * build). The first value this page trusts is its baseline; a later trusted
 * value that differs means a deploy happened while the page was open. That
 * alone is no reason to interrupt anyone: the page asks the browser for the
 * new service worker straight away (instead of waiting for the hourly check),
 * and the ordinary "a new version is ready" prompt follows if there is one.
 *
 * Only when a request then fails in a way nothing but a contract change
 * explains -- the server has no such route (404 or 405 with
 * `details.route: "unknown"`), or it refuses a JSON field this code sends
 * (`details.unknown_field`) -- does the page say it NEEDS to reload, and it
 * reloads on its own once nothing is at stake (`./busy`): when the tab is in
 * the background, or after {@link SKEW_RELOAD_CEILING_MS}, never while a
 * message is being written.
 *
 * # Which responses are trusted
 *
 * The service worker answers some API GETs from its cache when the network is
 * slow (NetworkFirst, `ols-api-cache` in vite.config.ts), and a cached response
 * carries the header of the build that cached it. Trusting those would flip
 * the baseline back and forth on every slow request. So only responses the
 * worker never caches count: any non-GET, and anything under `/auth/`
 * (whoami at boot is the usual baseline; after a deploy the backend restart
 * drops the event stream, and its reconnect's `POST /events/ticket` brings the
 * new value within seconds).
 *
 * # Why there is no loop guard
 *
 * After the reload the page starts over: its baseline is the NEW version, so
 * skew is false until the next deploy. A backend-only deploy (the bundle did
 * not change) therefore costs one quiet update check, and a reload only if a
 * contract error also happened -- after which the same bundle comes back with
 * a fresh baseline and nothing to compare.
 *
 * Known limit: a page whose very first trusted response is already from the
 * new backend (an installed app opened after a deploy) has nothing to compare.
 * That case is the launch-time silent update's (`./updates`,
 * maybeApplyAtLaunch).
 */
// Only `./busy` (no imports of its own): the transport imports this module,
// and `./updates` reaches the transport back through the error reporter, so
// the two actions that need `./updates` -- ask for the new worker, apply it --
// are handed in by `UpdatePrompt`, which owns both.
import { isAppIdle } from './busy';

export const APP_VERSION_HEADER = 'X-App-Version';

/** How long the "needs to reload" prompt waits for a hidden tab before reloading anyway (when idle). */
export const SKEW_RELOAD_CEILING_MS = 10 * 60 * 1000;
/** How often the reload watch looks again. */
export const SKEW_RELOAD_POLL_MS = 5_000;

export interface SkewState {
  /** The first trusted `X-App-Version` this page saw. */
  baseline?: string;
  /** A later trusted value differed from the baseline. */
  skew: boolean;
  /** A contract error arrived while skewed: this window must reload. */
  hard: boolean;
  /** When `hard` became true (epoch ms). */
  hardSince?: number;
}

const INITIAL: SkewState = { skew: false, hard: false };
let state: SkewState = INITIAL;
const listeners = new Set<() => void>();

function publish(next: Partial<SkewState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

export function subscribeToSkew(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSkewState(): SkewState {
  return state;
}

/** True when the service worker can never have answered this request from its cache. */
function cacheFree(method: string, path: string): boolean {
  return method.toUpperCase() !== 'GET' || path.startsWith('/auth/');
}

/**
 * Called by the transport for every response with the header's value, the
 * request's method and its API path (without the `/api/v1` prefix).
 */
export function noteAppVersion(value: string | null | undefined, method: string, path: string): void {
  if (!value || !cacheFree(method, path)) return;
  if (state.baseline === undefined) {
    publish({ baseline: value });
    return;
  }
  if (state.skew || value === state.baseline) return;
  publish({ skew: true });
  // Ask now rather than at the next hourly check: if the bundle changed too,
  // the ordinary prompt (or the launch-time silent update) takes it from here.
  onSkew?.();
}

let onSkew: (() => void) | undefined;

/**
 * What to do the moment skew is seen: `UpdatePrompt` passes
 * `checkForUpdate`. Returns a teardown.
 */
export function setSkewHandler(handler: () => void): () => void {
  onSkew = handler;
  return () => {
    if (onSkew === handler) onSkew = undefined;
  };
}

/** What the transport knows of a failed response; `ApiError` satisfies it. */
export interface FailedResponse {
  status: number;
  details?: Readonly<Record<string, unknown>> | undefined;
}

/** A refusal only a change of API contract explains (the markers of ADR 0103). */
export function isContractError(error: FailedResponse): boolean {
  const details = error.details ?? {};
  if ((error.status === 404 || error.status === 405) && details.route === 'unknown') return true;
  return error.status === 400 && typeof details.unknown_field === 'string';
}

/** Called by the transport for every failure it is about to throw. */
export function noteApiFailure(error: FailedResponse): void {
  if (!state.skew || state.hard) return;
  if (!isContractError(error)) return;
  publish({ hard: true, hardSince: Date.now() });
}

/**
 * While `hard`, reload as soon as nothing is at stake AND the person is not
 * looking (the tab is hidden), or once {@link SKEW_RELOAD_CEILING_MS} has
 * passed -- but never while something is held (a composer, unsaved settings,
 * a send in its Undo window, an Outbox flush), however long that takes.
 *
 * `reload` is `UpdatePrompt`'s `applyUpdateAndReload`, which takes a waiting
 * worker first when there is one. Called at most once. Returns a teardown.
 */
export function startSkewReloadWatch(reload: () => void): () => void {
  let done = false;
  const tick = (): void => {
    if (done || !state.hard) return;
    if (!isAppIdle()) return;
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const waited = Date.now() - (state.hardSince ?? Date.now());
    if (!hidden && waited < SKEW_RELOAD_CEILING_MS) return;
    done = true;
    reload();
  };
  const timer = window.setInterval(tick, SKEW_RELOAD_POLL_MS);
  document.addEventListener('visibilitychange', tick);
  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', tick);
  };
}

/** Test seam. */
export function resetSkewForTests(): void {
  state = INITIAL;
  listeners.clear();
  onSkew = undefined;
}
